import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { addNotification } from '../../../store/slices/uiSlice';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Area,
  AreaChart
} from 'recharts';

const ACTIVITY_PAGE_SIZE = 60;
const ACTIVITY_GAP_COMPRESSION_THRESHOLD_MINUTES = 30;

const EVENT_COLOR_LEGEND = [
  { label: 'Created / sent', className: 'bg-emerald-500 ring-emerald-200 dark:ring-emerald-900' },
  { label: 'Updated / read', className: 'bg-blue-500 ring-blue-200 dark:ring-blue-900' },
  { label: 'Removed / inactive', className: 'bg-rose-500 ring-rose-200 dark:ring-rose-900' },
  { label: 'Auth', className: 'bg-purple-500 ring-purple-200 dark:ring-purple-900' },
  { label: 'App / tab', className: 'bg-amber-500 ring-amber-200 dark:ring-amber-900' },
  { label: 'Other', className: 'bg-slate-400 ring-slate-200 dark:ring-slate-800' }
];

const formatEventName = (type = '') => String(type).toLowerCase().replace(/_/g, ' ');

const ENTITY_LABEL_KEYS = [
  'title',
  'name',
  'taskTitle',
  'messagePreview',
  'channelName',
  'announcementTitle',
  'documentTitle',
  'documentName',
  'viewName'
];

const isIdLikeMetadataKey = (key = '') => {
  const keyText = String(key);
  const normalizedKey = keyText.toLowerCase();
  return normalizedKey === 'id' || normalizedKey === 'addedby' || /(^|[_-])id$/i.test(keyText) || /Id$/.test(keyText);
};

const getEventTone = (type = '') => {
  const eventType = String(type);

  if (eventType.includes('DELETED') || eventType.includes('LEFT') || eventType.includes('INACTIVE') || eventType.includes('BACKGROUNDED')) {
    return {
      label: 'Removed / inactive',
      boxClass: 'bg-rose-500 hover:bg-rose-600 ring-rose-200 dark:ring-rose-900',
      timelineClass: 'text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-200 dark:bg-rose-900/25 dark:border-rose-800'
    };
  }

  if (eventType.includes('CREATED') || eventType.includes('SENT') || eventType.includes('JOINED') || eventType.includes('ASSIGNED')) {
    return {
      label: 'Created / sent',
      boxClass: 'bg-emerald-500 hover:bg-emerald-600 ring-emerald-200 dark:ring-emerald-900',
      timelineClass: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-200 dark:bg-emerald-900/25 dark:border-emerald-800'
    };
  }

  if (eventType.includes('UPDATED') || eventType.includes('CHANGED') || eventType.includes('ACKNOWLEDGED') || eventType.includes('READ')) {
    return {
      label: 'Updated / read',
      boxClass: 'bg-blue-500 hover:bg-blue-600 ring-blue-200 dark:ring-blue-900',
      timelineClass: 'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-200 dark:bg-blue-900/25 dark:border-blue-800'
    };
  }

  if (eventType.includes('LOGIN') || eventType.includes('LOGOUT') || eventType.startsWith('USER_')) {
    return {
      label: 'Auth',
      boxClass: 'bg-purple-500 hover:bg-purple-600 ring-purple-200 dark:ring-purple-900',
      timelineClass: 'text-purple-700 bg-purple-50 border-purple-200 dark:text-purple-200 dark:bg-purple-900/25 dark:border-purple-800'
    };
  }

  if (eventType.startsWith('APP_') || eventType.startsWith('TAB_')) {
    return {
      label: 'App / tab',
      boxClass: 'bg-amber-500 hover:bg-amber-600 ring-amber-200 dark:ring-amber-900',
      timelineClass: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-200 dark:bg-amber-900/25 dark:border-amber-800'
    };
  }

  return {
    label: 'Other',
    boxClass: 'bg-slate-400 hover:bg-slate-500 ring-slate-200 dark:ring-slate-800',
    timelineClass: 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600'
  };
};

const ActivityTracker = () => {
  const { token, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const loadingRef = useRef(false);


  const getMinuteBucketStart = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;

    date.setSeconds(0, 0);
    return date;
  };
  // Aggregate data for the Line chart
  const chartData = useMemo(() => {
    if (activities.length === 0) return [];

    // Group activities by minute for the chart
    const groupsByMinute = new Map();
    
    // Sort activities by timestamp ascending to ensure the chart timeline is correct
    const sortedActivities = [...activities].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    const formatMinuteGroupTime = (date) => {
      if (!date) return 'Unknown time';
  
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };
    
    sortedActivities.forEach((activity) => {
      const minuteStart = getMinuteBucketStart(activity.timestamp);
      if (!minuteStart) return;
      const key = minuteStart.getTime();

      if (!groupsByMinute.has(key)) {
        groupsByMinute.set(key, {
          time: formatMinuteGroupTime(minuteStart),
          count: 0,
          timestamp: key
        });
      }

      groupsByMinute.get(key).count += 1;
    });

    return Array.from(groupsByMinute.values());
  }, [activities]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch users for activity tracking'
      }));
    } finally {
      setLoadingUsers(false);
    }
  }, [token, dispatch]);

  const fetchActivities = useCallback(async (userId, pageNum = 0, append = false) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/activity/admin/logs`, {
        params: {
          userId: userId || undefined,
          page: pageNum,
          size: ACTIVITY_PAGE_SIZE
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newActivities = response.data.content;
      if (append) {
        setActivities(prev => [...prev, ...newActivities]);
      } else {
        setActivities(newActivities);
      }
      
      setHasMore(!response.data.last);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch activity logs'
      }));
    } finally {
      setLoading(false);
    }
  }, [token, dispatch]);

  useEffect(() => {
    fetchUsers();
    fetchActivities(); // Fetch all org activities initially
  }, [fetchUsers, fetchActivities]);

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUserId(userId);
    fetchActivities(userId, 0, false);
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchActivities(selectedUserId, page + 1, true);
    }
  };

  const refreshActivities = () => {
    fetchUsers();
    fetchActivities(selectedUserId, 0, false);
  };

  const toggleGroupExpansion = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    loadingRef.current = loading || loadingUsers;
  }, [loading, loadingUsers]);

  useEffect(() => {
    const refreshIfActivityTabIsVisible = () => {
      const isVisible = document.visibilityState === 'visible';
      const isFocused = typeof document.hasFocus !== 'function' || document.hasFocus();

      if (isVisible && isFocused && !loadingRef.current) {
        refreshActivities();
      }
    };

    const intervalId = window.setInterval(refreshIfActivityTabIsVisible, 60000);
    return () => window.clearInterval(intervalId);
  }, [selectedUserId]);

  const getEventIcon = (type) => {
    const eventType = String(type || '');
    if (eventType.startsWith('TASK_')) return '📋';
    if (eventType.startsWith('MESSAGE_')) return '💬';
    if (eventType.startsWith('CHANNEL_')) return '📢';
    if (eventType.startsWith('APP_') || eventType.startsWith('TAB_')) return '📱';
    if (eventType.startsWith('USER_')) return '🔐';
    return '🔹';
  };

  const getEventColorClass = (type) => {
    return getEventTone(type).timelineClass;
  };

  const getActivityUserName = (activity) => {
    if (!activity) return 'Unknown user';

    // 1. Priority: Robust metadata details (set by backend during log or enrichment)
    const metadata = activity.metadata || {};
    if (metadata.actorName && String(metadata.actorName).trim() !== '') {
      return metadata.actorName;
    }
    if (metadata.actorUsername && String(metadata.actorUsername).trim() !== '') {
      return `@${metadata.actorUsername}`;
    }

    // 2. Secondary: Check if it's the current user (Self)
    const userIdStr = String(activity.userId || '');
    if (userIdStr && user && String(user.id) === userIdStr) {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return fullName || user.username || 'You';
    }

    // 3. Third: Lookup from the fetched organization users list
    if (userIdStr) {
      const userObj = users.find(u => String(u.id) === userIdStr);
      if (userObj) {
        const fullName = `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim();
        return fullName || userObj.username || userIdStr;
      }
    }

    // 4. Last Resort: ID or placeholder
    return activity.userId || 'Unknown user';
  };

  const getActivityEntityLabel = (activity) => {
    const eventType = String(activity.eventType || '');
    if (eventType.startsWith('MESSAGE_')) return 'Message';

    const metadata = activity.metadata || {};
    const labelKey = ENTITY_LABEL_KEYS.find(key => {
      const value = metadata[key];
      return value !== undefined && value !== null && String(value).trim() !== '';
    });

    if (labelKey) {
      return String(metadata[labelKey]);
    }

    if (eventType.startsWith('TASK_')) return 'Task';
    if (eventType.startsWith('CHANNEL_')) return 'Channel';
    if (eventType.startsWith('ANNOUNCEMENT_')) return 'Announcement';
    return null;
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'ANDROID': return <span title="Android">🤖</span>;
      case 'IOS': return <span title="iOS">🍎</span>;
      case 'WEB': return <span title="Web">🌐</span>;
      default: return <span title="Backend">⚙️</span>;
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

 



  const formatMinuteGroupRelative = (date) => {
    if (!date) return '';

    const diffInMinutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
    if (diffInMinutes === 0) return 'This minute';
    if (diffInMinutes === 1) return '1 min ago';
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours === 1) return '1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    return '';
  };

  const formatGapDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours < 24) {
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  const createCompressedGapGroups = (newerTime, gapMinutes) => {
    const groups = [];
    let remainingMinutes = gapMinutes;
    let cursor = new Date(newerTime.getTime() - 60000);

    while (remainingMinutes > 0) {
      let chunkMinutes;
      if (remainingMinutes >= 1440) {
        chunkMinutes = 1440;
      } else if (remainingMinutes >= 60) {
        chunkMinutes = 60;
      } else if (remainingMinutes > ACTIVITY_GAP_COMPRESSION_THRESHOLD_MINUTES) {
        chunkMinutes = ACTIVITY_GAP_COMPRESSION_THRESHOLD_MINUTES;
      } else {
        chunkMinutes = remainingMinutes;
      }

      const chunkNewerTime = new Date(cursor);
      const chunkOlderTime = new Date(cursor.getTime() - ((chunkMinutes - 1) * 60000));

      groups.push({
        type: 'gap',
        key: `gap-${chunkOlderTime.getTime()}-${chunkNewerTime.getTime()}`,
        gapMinutes: chunkMinutes,
        newerTime: chunkNewerTime,
        olderTime: chunkOlderTime
      });

      cursor = new Date(chunkOlderTime.getTime() - 60000);
      remainingMinutes -= chunkMinutes;
    }

    return groups;
  };

  const groupActivitiesByMinute = (activityList) => {
    if (activityList.length === 0) return [];

    const groupsByMinute = new Map();

    activityList.forEach((activity, index) => {
      const minuteStart = getMinuteBucketStart(activity.timestamp);
      if (!minuteStart) return;
      const key = String(minuteStart.getTime());

      if (!groupsByMinute.has(key)) {
        groupsByMinute.set(key, {
          type: 'activity',
          key,
          minuteStart,
          activities: []
        });
      }

      groupsByMinute.get(key).activities.push(activity);
    });

    const sortedGroups = Array.from(groupsByMinute.values()).sort((a, b) => {
      return b.minuteStart.getTime() - a.minuteStart.getTime();
    });

    return sortedGroups.reduce((items, group, index) => {
      items.push(group);

      const nextGroup = sortedGroups[index + 1];
      if (nextGroup) {
        const gapMinutes = Math.floor((group.minuteStart.getTime() - nextGroup.minuteStart.getTime()) / 60000) - 1;
        if (gapMinutes > ACTIVITY_GAP_COMPRESSION_THRESHOLD_MINUTES) {
          items.push(...createCompressedGapGroups(group.minuteStart, gapMinutes));
        } else {
          for (let minuteOffset = 1; minuteOffset <= gapMinutes; minuteOffset += 1) {
            const emptyMinute = new Date(group.minuteStart.getTime() - (minuteOffset * 60000));
            items.push({
              type: 'activity',
              key: `empty-${emptyMinute.getTime()}`,
              minuteStart: emptyMinute,
              activities: []
            });
          }
        }
      }

      return items;
    }, []);
  };

  const formatMetadata = (metadata) => {
    if (!metadata) return null;
    return Object.entries(metadata)
      .filter(([key]) => !ENTITY_LABEL_KEYS.includes(key) && !isIdLikeMetadataKey(key))
      .map(([key, value]) => (
        <span key={key} className="text-[10px] text-gray-400 dark:text-gray-500 mr-2">
          <span className="font-medium">{key}:</span> {String(value)}
        </span>
      ));
  };

  // const renderMetadataRows = (metadata) => {
  //   if (!metadata || Object.keys(metadata).length === 0) {
  //     return (
  //       <span className="text-gray-400 dark:text-gray-500">No extra details</span>
  //     );
  //   }

  //   const visibleEntries = Object.entries(metadata)
  //     .filter(([key]) => !ENTITY_LABEL_KEYS.includes(key) && !isIdLikeMetadataKey(key));

  //   if (visibleEntries.length === 0) {
  //     return (
  //       <span className="text-gray-400 dark:text-gray-500">No extra details</span>
  //     );
  //   }

  //   return visibleEntries.map(([key, value]) => (
  //     <div key={key} className="flex justify-between gap-3 text-[11px]">
  //       <span className="shrink-0 font-medium text-gray-500 dark:text-gray-400">{key}</span>
  //       <span className="min-w-0 truncate text-gray-700 dark:text-gray-200">{String(value)}</span>
  //     </div>
  //   ));
  // };

  const renderLoadMoreButton = (className = '') => (
    hasMore && (
      <div className={`mt-6 flex justify-center ${className}`}>
        <button
          onClick={loadMore}
          disabled={loading}
          className="text-xs font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center space-x-1 py-2 px-4 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></div>
          ) : (
            <span>↓ Load older activity</span>
          )}
        </button>
      </div>
    )
  );

  const renderChartView = () => (
    <div className="flex-grow p-3 sm:p-4 min-w-0">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Activity Pulse</h3>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Activity frequency over time</p>
        </div>
      </div>
      
      <div className="h-[350px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" className="dark:stroke-gray-700" strokeOpacity={0.5} />
            <XAxis 
              dataKey="time" 
              fontSize={9} 
              tickLine={false} 
              axisLine={false}
              tick={{ fill: '#9ca3af' }}
              minTickGap={40}
            />
            <YAxis 
              fontSize={9} 
              tickLine={false} 
              axisLine={false}
              tick={{ fill: '#9ca3af' }}
              allowDecimals={false}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                borderRadius: '6px', 
                border: '1px solid #e5e7eb',
                fontSize: '11px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                padding: '8px'
              }}
              itemStyle={{ color: '#0ea5e9', fontWeight: 'bold', padding: 0 }}
              labelStyle={{ color: '#374151', marginBottom: '2px', fontWeight: 'bold' }}
            />
            <Area 
              type="monotone" 
              dataKey="count" 
              stroke="#0ea5e9" 
              strokeWidth={2}
              fillOpacity={1} 
              fill="url(#colorCount)" 
              animationDuration={1000}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const renderTimelineView = () => (
    <div className="w-full lg:w-[160px] xl:w-[200px] flex-shrink-0 border-t lg:border-t-0 lg:border-l border-gray-100 dark:border-gray-700/50 bg-gray-50/20 dark:bg-gray-900/5 flex flex-col h-[400px] lg:h-auto">
      <div className="p-3 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
        <h3 className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-tight">Timeline</h3>
      </div>
      
      <div className="relative flex-grow overflow-y-auto p-3 custom-scrollbar">
        <div className="absolute left-[1.55rem] top-6 bottom-6 w-px bg-gray-200 dark:bg-gray-700 opacity-50"></div>
        
        <div className="space-y-3">
          {activities.map((activity, index) => {
            const userName = getActivityUserName(activity);
            const colorClass = getEventColorClass(activity.eventType);
            const entityLabel = getActivityEntityLabel(activity);
            
            return (
              <div key={activity.id || `${activity.timestamp}-${index}`} className="relative flex items-start group">
                <div className={`z-10 flex items-center justify-center w-5 h-5 rounded-full border shadow-sm shrink-0 transition-transform group-hover:scale-110 ${colorClass}`}>
                  <span className="text-[9px]">{getEventIcon(activity.eventType)}</span>
                </div>
                
                <div className="ml-2.5 flex-grow min-w-0">
                  <div className="flex flex-col">
                    <div className="text-[11px] text-gray-900 dark:text-gray-100 leading-none truncate font-semibold" title={!selectedUserId ? userName : formatEventName(activity.eventType)}>
                      {!selectedUserId ? userName : formatEventName(activity.eventType)}
                    </div>
                    {!selectedUserId && (
                      <div className="text-[9px] text-gray-500 dark:text-gray-400 capitalize truncate mt-0.5">
                        {formatEventName(activity.eventType)}
                      </div>
                    )}
                    {entityLabel && (
                      <div className="mt-0.5 truncate">
                        <span className="text-[8px] font-medium text-primary-600 bg-primary-50 dark:text-primary-300 dark:bg-primary-900/30 px-1 rounded-sm" title={entityLabel}>
                          {entityLabel}
                        </span>
                      </div>
                    )}
                    <div className="text-[8px] text-gray-400 dark:text-gray-500 tabular-nums mt-0.5">
                      {formatDate(activity.timestamp).split(',')[1] || formatDate(activity.timestamp)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {renderLoadMoreButton('mt-3')}
      </div>
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-visible">
      <div className="p-2.5 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-2 lg:flex-row lg:justify-between lg:items-center bg-gray-50/50 dark:bg-gray-900/50">
        <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
          Live Activity
        </h2>
        
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select
            id="user-select"
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-[11px] rounded-md focus:ring-primary-500 focus:border-primary-500 block w-full p-1 sm:w-48"
            value={selectedUserId}
            onChange={handleUserChange}
            disabled={loadingUsers}
          >
            <option value="">All Users</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={refreshActivities}
            disabled={loading || loadingUsers}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-2.5 py-1 text-[10px] font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          >
            {loading || loadingUsers ? '...' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading && activities.length === 0 ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-10 text-xs text-gray-500 dark:text-gray-400">
          No activity found.
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row lg:h-[400px]">
          {renderChartView()}
          {renderTimelineView()}
        </div>
      )}
    </div>
  );
};

export default ActivityTracker;
